import i18n from "src/i18n/config";
import {
  formatNumberDigits,
  isArabicLanguage,
  toArabicIndicDigitsInString,
  wrapBidiIsolate,
} from "src/utils/numberFormat";

/**
 * Locale-aware numeric primitives shared by every Paid Media surface (overview cards, Meta Ads
 * and Google Ads reports).
 *
 * The active language is read from the i18n singleton rather than threaded through each call
 * site: these are used from well over a hundred places across the Paid Media tables, charts and
 * cards, and every one of those components already subscribes to `languageChanged` through
 * `useTranslation`, so they re-render — and re-format — when the language changes.
 */
export const paidMediaLanguage = (): string =>
  i18n.resolvedLanguage ?? i18n.language ?? "en";

/**
 * Wraps a numeral run as a single bidi isolate for Arabic. A number and a unit made of letters
 * (`x`, `K`, `M`, a currency code) have to be inside the same isolate, otherwise the bidi
 * algorithm can reorder the unit away from its digits in an RTL paragraph.
 *
 * Percentages are the exception and must NOT be isolated — see `formatPaidMediaPercent`.
 */
export const paidMediaNumericToken = (text: string): string =>
  isArabicLanguage(paidMediaLanguage()) ? wrapBidiIsolate(text) : text;

/** Locale-grouped digits with no bidi wrapper, so a unit can be appended before wrapping. */
export const paidMediaDigits = (
  value: number,
  options?: Intl.NumberFormatOptions
): string => formatNumberDigits(value, options ?? {}, paidMediaLanguage());

export const paidMediaFixedDigits = (value: number, fractionDigits: number): string =>
  paidMediaDigits(value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

/** Comma-grouped whole count, e.g. `198,650` / `١٩٨٬٦٥٠`. */
export const formatPaidMediaCount = (value: number): string =>
  paidMediaNumericToken(paidMediaDigits(value));

/**
 * Percentage with an explicit precision, e.g. `3.87%` / `%٣٫٨٧`.
 *
 * Left unisolated on purpose: a trailing `%` is a bidi ET, which binds to Western (EN) digits but
 * not to Eastern Arabic-Indic (AN) ones, so in Arabic it resolves against the RTL paragraph and
 * renders on the *left* of the digits — the Arabic convention, matching Sales & Products.
 */
export const formatPaidMediaPercent = (value: number, fractionDigits = 2): string =>
  `${paidMediaFixedDigits(value, fractionDigits)}%`;

/** ROAS multiplier, e.g. `5.93x` / `٥٫٩٣x`. */
export const formatPaidMediaRoas = (value: number, fractionDigits = 2): string =>
  paidMediaNumericToken(`${paidMediaFixedDigits(value, fractionDigits)}x`);

/**
 * Localizes a date/datetime string's digits for Arabic. Keeps the source shape
 * (`YYYY-MM-DD`, `MM/DD`, ISO with time) so API values stay recognizable.
 */
export const formatPaidMediaDateText = (value: string | null | undefined): string => {
  const trimmed = value?.trim();
  if (!trimmed) {
    return "";
  }
  const normalized = trimmed.replace("T", " ");
  return isArabicLanguage(paidMediaLanguage())
    ? toArabicIndicDigitsInString(normalized)
    : normalized;
};
